import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hardwareDevices } from "../../actions/doctorActions";
import Table from "../react-table-component/ReactTable";
import { formatDate } from "../../helper";
import { Link } from "react-router-dom";
import { DropDownItem } from "../header/Header";
import historyIcon from "../../assets/img/svg/history-icon.svg";
import editIcon from "../../assets/img/svg/edit-icon.svg";
import dotMenuIcon from "../../assets/img/svg/dots-menu-icon.svg";
import trashIcon from "../../assets/img/svg/trash-can-2.svg";
import { HardwareDeleteConfirmModal } from "./HardwareDeleteConfirmModal";

export default function HardwareList() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.loginData.token);
  const { loading, devices } = useSelector(
    (state) => state.hardwareDevicesList
  );

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [id, setId] = useState(null);

  const [searchField, setSearchField] = useState("");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const simulateProgress = () => {
      let interval = setInterval(() => {
        setProgress((prev) => (prev >= 100 ? 100 : prev + 20));
      }, 400);
      return () => clearInterval(interval);
    };
    simulateProgress();
    dispatch(hardwareDevices(token)).finally(() => setProgress(100));
  }, [dispatch, token]);

  const handleSearch = (e) => setSearchField(e.target.value.toLowerCase());

  const filteredDevices = React.useMemo(() => {
    return (
      devices?.filter((item) =>
        item.serial_number?.toLowerCase()?.includes(searchField)
      ) || []
    );
  }, [devices, searchField]);

  const dotMenuRef = useRef(null);
  const [openMenuRowIndex, setOpenMenuRowIndex] = useState(null);
  const toggleMenuForRow = (rowIndex) => {
    setOpenMenuRowIndex((prevRowIndex) =>
      prevRowIndex === rowIndex ? null : rowIndex
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dotMenuRef.current &&
        !dotMenuRef.current.contains(event.target) &&
        openMenuRowIndex !== null
      ) {
        setOpenMenuRowIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuRowIndex]);

  const columns = React.useMemo(() => [
    { Header: "Serial No", accessor: "serial_number" },
    { Header: "Batch No", accessor: "batch_number" },
    { Header: "BLE Address", accessor: "ble_address" },
    {
      Header: "Manufacture Date",
      accessor: "manufacture_date",
      Cell: ({ value }) => (value ? formatDate(value) : "N/A"),
    },
    { Header: "Hardware Rev", accessor: "hardware_revision" },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => value || "N/A",
    },
    {
      Header: "Passed Test",
      accessor: "passed_testing",
      Cell: ({ value }) => (value ? "Yes" : "No"),
    },
    {
      Header: "Is Damaged",
      accessor: "is_damaged",
      Cell: ({ value }) => (value ? "Yes" : "No"),
    },
    {
      Header: "",
      accessor: "action",
      disableSortBy: true,
      Cell: ({ row }) => {
        const isMenuOpen = openMenuRowIndex === row.index;
        return (
          <div className="dot-menu-icon">
            <img
              src={dotMenuIcon}
              alt="dot menu"
              onClick={() => toggleMenuForRow(row.index)}
            />

            {isMenuOpen && (
              <div ref={dotMenuRef} className="patients-dot-menu">
                <DropDownItem
                  item={{
                    title: "View",
                    icon: historyIcon,
                    to: `/hardware/${row?.original?.id}`,
                  }}
                />
                <DropDownItem
                  item={{
                    title: "Edit",
                    icon: editIcon,
                    to: `/hardware-update/${row?.original?.id}`,
                  }}
                />
                <div className="delete-clr">
                  <DropDownItem
                    item={{
                      title: "Delete",
                      icon: trashIcon,
                    }}
                    handleClick={() => {
                      setId(row?.original?.id);
                      setShowDeleteDialog(true);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        );
      },
    },
  ]);

  return (
    <div style={{ padding: "30px" }}>
      <HardwareDeleteConfirmModal
        show={showDeleteDialog}
        handleClose={() => {
          setShowDeleteDialog(false);
        }}
        id={id}
        token={token}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <h3 style={{ margin: 0 }}>Hardware List</h3>
        <Link to={`/hardware-create`}>
          <button
            className="button btn-default"
            style={{
              padding: "0.35rem 0.7rem",
              fontSize: "0.9rem",
              fontWeight: "bold",
            }}
          >
            Add Device
          </button>
        </Link>
      </div>

      <Table
        columns={columns}
        data={filteredDevices}
        loading={loading}
        tableOf="HardwareDevices"
        progress={progress}
      />
    </div>
  );
}
